import type { ClientProfile, SubscriptionData, UsageStats, PlanData } from '../types';

interface Props {
  profile: ClientProfile;
  subscription: SubscriptionData | null;
  usage: UsageStats | null;
  plans: PlanData[];
}

const fmtLimit = (n: number | null | undefined) => (n === -1 || n == null ? '∞' : String(n));

export default function OverviewTab({ profile, subscription, usage, plans }: Props) {
  const plan = plans.find((p) => p.id === subscription?.plan_id);

  const usageItems = usage
    ? [
        { label: 'Contas', icon: 'ri-bank-line', val: usage.accounts, max: plan?.accounts_limit },
        { label: 'Cartões', icon: 'ri-bank-card-line', val: usage.cards, max: plan?.cards_limit },
        { label: 'Metas', icon: 'ri-flag-line', val: usage.goals, max: plan?.goals_limit },
        { label: 'Lançamentos/mês', icon: 'ri-exchange-line', val: usage.monthly_transactions, max: plan?.monthly_transactions_limit },
      ]
    : [];

  return (
    <div className="grid grid-cols-3 gap-5">
      {/* Left: usage + subscription */}
      <div className="col-span-2 space-y-5">
        {/* Usage */}
        {usage && (
          <div>
            <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-3">Uso do Plano Atual</h3>
            <div className="grid grid-cols-2 gap-3">
              {usageItems.map((item) => {
                const unlimited = item.max === -1 || item.max == null;
                const pct = unlimited ? 0 : Math.min(100, (item.val / (item.max ?? 1)) * 100);
                const over = !unlimited && item.val >= (item.max ?? 0);
                return (
                  <div key={item.label} className={`bg-white/5 border rounded-xl p-4 ${over ? 'border-red-500/30' : 'border-white/8'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${over ? 'bg-red-500/20' : 'bg-white/8'}`}>
                          <i className={`${item.icon} text-[14px] ${over ? 'text-red-400' : 'text-white/50'}`} />
                        </div>
                        <span className="text-[12px] text-white/50">{item.label}</span>
                      </div>
                      <span className={`text-[20px] font-700 ${over ? 'text-red-400' : 'text-white'}`}>
                        {item.val}
                        <span className="text-[12px] font-400 text-white/30 ml-1">/ {fmtLimit(item.max)}</span>
                      </span>
                    </div>
                    {!unlimited && (
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${over ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-violet-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                    {unlimited && <div className="h-1.5 bg-white/5 rounded-full" />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Subscription summary */}
        <div>
          <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-3">Assinatura</h3>
          {subscription ? (
            <div className="bg-white/5 border border-white/8 rounded-xl p-4 grid grid-cols-3 gap-4">
              {[
                { label: 'Plano', val: subscription.plan_name },
                { label: 'Valor', val: `R$ ${Number(subscription.amount).toFixed(2)}/mês` },
                { label: 'Status', val: subscription.status === 'active' ? 'Ativa' : subscription.status === 'trial' ? 'Trial' : subscription.status },
                { label: 'Início', val: subscription.started_at ? new Date(subscription.started_at).toLocaleDateString('pt-BR') : '—' },
                { label: 'Vencimento', val: subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString('pt-BR') : 'Sem data' },
                { label: 'Auto renovação', val: subscription.auto_renew ? 'Sim' : 'Não' },
              ].map((item) => (
                <div key={item.label} className="text-center py-2">
                  <p className="text-[11px] text-white/40 mb-1">{item.label}</p>
                  <p className="text-[14px] font-600 text-white">{item.val}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white/5 border border-white/8 rounded-xl p-6 text-center">
              <i className="ri-vip-crown-line text-2xl text-white/20 mb-1" />
              <p className="text-[13px] text-white/30">Sem assinatura ativa — plano Free</p>
            </div>
          )}
        </div>

        {/* Last activity */}
        {usage?.last_transaction_date && (
          <div className="bg-white/5 border border-white/8 rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-violet-500/20">
              <i className="ri-exchange-line text-violet-400 text-[16px]" />
            </div>
            <div>
              <p className="text-[13px] font-600 text-white">Último lançamento</p>
              <p className="text-[12px] text-white/40">{new Date(usage.last_transaction_date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
        )}
      </div>

      {/* Right: quick info */}
      <div className="space-y-4">
        <div className="bg-white/5 border border-white/8 rounded-xl p-4">
          <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-3">Resumo da Conta</h3>
          <div className="space-y-2.5 text-[13px]">
            {[
              { label: 'E-mail', val: profile.email },
              { label: 'Telefone', val: profile.phone || <span className="text-white/30">—</span> },
              { label: 'CPF', val: profile.cpf || <span className="text-white/30">—</span> },
              { label: 'Cidade', val: profile.city ? `${profile.city}${profile.state ? `/${profile.state}` : ''}` : <span className="text-white/30">—</span> },
              { label: 'Auth', val: profile.auth_provider === 'google' ? 'Google' : 'E-mail/Senha' },
              { label: 'PIN', val: profile.pin_enabled ? 'Ativado' : 'Desativado' },
              { label: 'Onboarding', val: <span className={profile.wizard_completed ? 'text-emerald-400' : 'text-orange-400'}>{profile.wizard_completed ? 'Concluído' : 'Pendente'}</span> },
            ].map((item, i) => (
              <div key={i} className="flex justify-between items-center gap-2">
                <span className="text-white/40 shrink-0">{item.label}</span>
                <span className="text-white font-500 text-right truncate max-w-[150px]">{item.val as any}</span>
              </div>
            ))}
          </div>
        </div>

        {(profile.lead_source || profile.utm_source) && (
          <div className="bg-white/5 border border-white/8 rounded-xl p-4">
            <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-3">Marketing</h3>
            <div className="space-y-2 text-[13px]">
              {profile.lead_source && (
                <div className="flex justify-between">
                  <span className="text-white/40">Origem</span>
                  <span className="text-white font-500">{profile.lead_source}</span>
                </div>
              )}
              {profile.utm_source && (
                <div className="flex justify-between">
                  <span className="text-white/40">UTM Source</span>
                  <span className="text-white font-500">{profile.utm_source}</span>
                </div>
              )}
              {profile.utm_campaign && (
                <div className="flex justify-between">
                  <span className="text-white/40">Campanha</span>
                  <span className="text-white font-500 truncate max-w-[120px]">{profile.utm_campaign}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {profile.internal_notes && (
          <div className="bg-white/5 border border-white/8 rounded-xl p-4">
            <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-2">Notas Internas</h3>
            <p className="text-[12px] text-white/60 leading-relaxed line-clamp-5">{profile.internal_notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
